// -----------------------------------------------------------------------------
// preflight/capabilities.ts — turn raw Capabilities into runtime decisions.
//
// detect.ts answers "what CAN this browser do?". This file answers "given that,
// HOW should we run?". It derives one RuntimeConfig the rest of the app reads:
//   • which preview proxy resolution to render at (RAM/cores dependent),
//   • which export container + codecs to use (encode support dependent),
//   • whether scratch storage uses OPFS or falls back to IndexedDB.
//
// Keeping this logic in one place means the engine, exporter and storage layers
// never re-interrogate the browser — they just read config.
// -----------------------------------------------------------------------------
import type { Capabilities } from './detect';

// Which container/codecs the exporter will target.
export type ExportContainer = 'mp4' | 'webm';
export type ExportVideoCodec = 'avc' | 'vp9'; // Mediabunny short codec ids
export type ExportAudioCodec = 'aac' | 'opus' | null;

export interface RuntimeConfig {
  // 0..1 multiplier applied to the project resolution for the live preview.
  // Lower on memory-constrained machines so scrubbing stays smooth.
  previewScale: number;
  // Hard cap on preview proxy width in pixels (keeps 4K projects affordable).
  previewMaxWidth: number;
  // Export target chosen from native encode support (Tier 0 = native only).
  exportContainer: ExportContainer;
  exportVideoCodec: ExportVideoCodec;
  exportAudioCodec: ExportAudioCodec;
  // Scratch storage backend.
  useOpfs: boolean;
}

export function deriveRuntimeConfig(caps: Capabilities): RuntimeConfig {
  // --- Preview proxy resolution ------------------------------------------------
  // Default to full-res preview; scale down when RAM is low or cores are few.
  const ram = caps.deviceMemoryGB ?? 8;
  const cores = caps.cores ?? 4;
  let previewScale = 1;
  if (ram < 4 || cores <= 2) previewScale = 0.5;
  else if (ram < 8 || cores <= 4) previewScale = 0.75;
  const previewMaxWidth = ram < 4 ? 854 : ram < 8 ? 1280 : 1920;

  // --- Export path (native WebCodecs only this tier) ---------------------------
  // Prefer MP4 (H.264 + AAC) where the browser can encode both natively.
  // Otherwise fall back to WebM (VP9 + Opus). The FFmpeg.wasm bridge that would
  // let Firefox emit AAC in an MP4 is Tier 1 — until then we degrade to WebM.
  let exportContainer: ExportContainer = 'webm';
  let exportVideoCodec: ExportVideoCodec = 'vp9';
  let exportAudioCodec: ExportAudioCodec = caps.audioEncodeOpus ? 'opus' : null;

  if (caps.videoEncodeSW && caps.audioEncodeAAC) {
    // Full native MP4 path (Chrome/Edge/Safari 26+).
    exportContainer = 'mp4';
    exportVideoCodec = 'avc';
    exportAudioCodec = 'aac';
  } else if (caps.videoEncodeSW && caps.audioEncodeOpus) {
    // H.264 encodes but no AAC (Firefox): use WebM/VP9/Opus for a clean native
    // file rather than an MP4 we can't give valid audio to yet.
    exportContainer = 'webm';
    exportVideoCodec = 'vp9';
    exportAudioCodec = 'opus';
  }

  return {
    previewScale,
    previewMaxWidth,
    exportContainer,
    exportVideoCodec,
    exportAudioCodec,
    useOpfs: caps.opfs,
  };
}
